import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop, Divider } from "@material-ui/core";
import { useFormik } from "formik";
import moment from "moment";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonLoader from "src/components/Loader/CommonLoader";
import { useAppSelector } from "src/app-redux/hooks";
import { selectAllPartyNameRowsList } from "src/app-redux/configuration/configurationSlice";
import { getAllPartName } from "src/app-redux/configuration/actions/configurationActions";
import { useStyles } from "./style";

export function CommercialReports(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const [loader, setLoader] = useState<boolean>(false);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);

  const moduleName = "Commercial Reports";
  const pageHeading = "Commercial Reports";

  const validationSchema = Yup.object().shape({
    ForwarderId: Yup.number().required("This field is required"),
    startDate: Yup.string().required("This field is required"),
    endDate: Yup.string().required("This field is required"),
  });

  const formik = useFormik({
    initialValues: {
      ForwarderId: "" as number | "",
      startDate: moment(new Date().setHours(0, 0, 0, 0)).format("YYYY-MM-DDTHH:mm"),
      endDate: moment().format("YYYY-MM-DDTHH:mm"),
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoader(true);
      const idToken = localStorage.getItem("token");
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          startDate: values.startDate,
          endDate: values.endDate,
          ForwarderId: Number(values.ForwarderId),
        }),
      };

      fetch(`${process.env.REACT_APP_API_URL}/get-statement_of_account-report`, requestOptions)
        .then((response) => {
          if (response.status === 200) return response.blob();
          throw response;
        })
        .then((data) => {
          const blob = new Blob([data], { type: "application/pdf" });
          const blobURL = URL.createObjectURL(blob);
          const iframe = document.createElement("iframe");
          document.body.appendChild(iframe);
          iframe.style.display = "none";
          iframe.src = blobURL;
          iframe.onload = function () {
            setTimeout(function () {
              iframe.focus();
              iframe.contentWindow?.print();
            }, 1);
          };
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    },
  });

  useEffect(() => {
    dispatch(getAllPartName(false));
  }, [dispatch]);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Box m={2} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid md={8}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}>{pageHeading}</Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            item
            xs={12}
            className={classes.outer}
            container
            direction="row"
            spacing={2}
            alignItems="flex-start"
          >
            <Grid item xs={12} md={4}>
              <Box className={classes.fieldBox}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Forwarder"
                  placeholder="Select Forwarder"
                  name="ForwarderId"
                  optionsType={allPartyNameRows}
                  getOptionLabel={(option: any) => option.name || ""}
                  value={
                    allPartyNameRows.find(
                      (party: any) => String(party.id) === String(formik.values.ForwarderId)
                    ) || null
                  }
                  onChange={(option: any) => {
                    formik.setFieldValue("ForwarderId", option?.id ?? "");
                  }}
                  error={formik.touched.ForwarderId && Boolean(formik.errors.ForwarderId)}
                  helperText={formik.touched.ForwarderId && formik.errors.ForwarderId}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className={classes.fieldBox}>
                <CommonTextFieldDate
                  label="Start Date"
                  name="startDate"
                  cssClassName="customTextField"
                  value={formik.values.startDate}
                  datePickerRef={startDatePickerRef}
                  onChange={(e: any) => {
                    formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                    formik.setFieldValue("endDate", "");
                  }}
                  error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                  helperText={formik.touched.startDate && formik.errors.startDate}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className={classes.fieldBox}>
                <CommonTextFieldDate
                  label="End Date"
                  name="endDate"
                  cssClassName="customTextField"
                  value={formik.values.endDate}
                  datePickerRef={endDatePickerRef}
                  onChange={(e: any) => {
                    formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  }}
                  error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  minDate={formik.values.startDate}
                  maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box className={classes.buttonBox}>
                <CommonButton
                  isGreen
                  cssClass={classes.printButton}
                  onClick={() => formik.submitForm()}
                >
                  Print Report
                </CommonButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
